import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FormEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import type { AnyAction, Dispatch, Store } from "redux";
import type { StudentSimulatorAppView } from "../../shared/domain/index.js";
import { appsForProfile, createSimulatorRegistry, findAppById, profileForMachine, resolveRunAlias } from "../apps/registry.js";
import { AppSurface } from "../apps/AppSurface.js";
import { buildSimulatorCommandRequest } from "../contracts/appCommand.js";
import type { SimulatorAppDefinition, SimulatorCommandInput, SimulatorCommandResult, SimulatorLaunchSource, SimulatorProfileId } from "../contracts/appManifest.js";
import { getSimulatorProfile } from "../profiles/index.js";
import type { ShellCommandLogEntry, ShellPhase } from "../state/shellState.js";
import { firstDeskAppIdForDonorAction, isWin11NativeDonorApp, toWin11DonorApp, win11ProfileDesktopApps, win11ProfilePinnedApps } from "./adapters/appRegistryAdapter.js";
import type { Win11DonorAppDescriptor } from "./adapters/appRegistryAdapter.js";
import rawStore from "./donor/reducers/index.js";
import DonorTaskbar from "./donor/components/taskbar/index.jsx";
import DonorActMenu from "./donor/components/menu/index.jsx";
import { CalnWid, DesktopApp, SidePane, StartMenu, WidPane } from "./donor/components/start/index.jsx";
import { Background, BootScreen, LockScreen as DonorLockScreen } from "./donor/containers/background/index.jsx";
import { Calculator } from "./donor/containers/applications/apps/calculator.jsx";
import { Camera } from "./donor/containers/applications/apps/camera.jsx";
import { BrowserApp } from "./donor/containers/applications/apps/browser.jsx";
import { Explorer } from "./donor/containers/applications/apps/explorer.jsx";
import { Getstarted } from "./donor/containers/applications/apps/getstarted.jsx";
import { CatalogApp } from "./donor/containers/applications/apps/catalog.jsx";
import { Notepad } from "./donor/containers/applications/apps/notepad.jsx";
import { Settings } from "./donor/containers/applications/apps/settings.jsx";
import { Taskmanager } from "./donor/containers/applications/apps/taskmanager.jsx";
import { WnTerminal } from "./donor/containers/applications/apps/terminal.jsx";
import { WhiteBoard } from "./donor/containers/applications/apps/whiteboard.jsx";
import { ToolBar } from "./donor/utils/general.jsx";
import "../simulator.css";
import "./donor/containers/applications/tabs.scss";
import "./donor/containers/applications/tabs2.scss";
import "./donor/containers/applications/wnapp.scss";
import "./Win11SimulatorShell.css";

const win11Store = rawStore as Store<unknown, AnyAction>;

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    lng: "en",
    resources: { en: { translation: {} } }
  });
}

type BridgeWindow = Window & {
  fdBridge?: {
    dispatchV2Command?: (
      appId: string,
      commandType: string,
      capability: string,
      payload: Record<string, unknown>
    ) => Promise<SimulatorCommandResult>;
  };
  __fdAttemptId?: string;
};

interface DonorWallpaperState {
  booted: boolean;
  dir: number;
  locked: boolean;
}

interface DonorAppWindowState extends Win11DonorAppDescriptor {
  dim?: CSSProperties;
  hide?: boolean;
  max?: boolean | null;
  size?: "full" | "mini" | "cstm";
  task?: boolean;
  z?: number;
}

type DonorAppsState = Record<string, DonorAppWindowState | number | undefined> & {
  hz: number;
};

interface DonorRootState {
  apps: DonorAppsState;
  wallpaper: DonorWallpaperState;
}

export interface FirstDeskSimulatorShellProps {
  activeMachine: string;
  attemptId: string;
  initialShellPhase?: ShellPhase;
  onCommandComplete?: (result: SimulatorCommandResult) => void;
  onCommandMessage?: (message: string) => void;
  profileId?: SimulatorProfileId;
  simulatorApps: StudentSimulatorAppView[];
  ticketId: string;
}

function commandResult(input: {
  body?: unknown;
  ok: boolean;
  requestId: string;
  status: number;
}): SimulatorCommandResult {
  return {
    body: input.body,
    ok: input.ok,
    requestId: input.requestId,
    status: input.status,
    type: "SIMULATOR_COMMAND_RESULT"
  };
}

export function FirstDeskSimulatorShell(props: FirstDeskSimulatorShellProps) {
  return (
    <Provider store={win11Store}>
      <Win11SimulatorShellInner {...props} />
    </Provider>
  );
}

function Win11SimulatorShellInner({
  activeMachine,
  attemptId,
  initialShellPhase = "locked",
  onCommandComplete,
  onCommandMessage,
  profileId,
  simulatorApps,
  ticketId
}: FirstDeskSimulatorShellProps) {
  const dispatch = useDispatch<Dispatch>();
  const wall = useSelector((state: DonorRootState) => state.wallpaper);
  const resolvedProfileId = profileId ?? profileForMachine(activeMachine);
  const profile = getSimulatorProfile(resolvedProfileId);
  const registry = useMemo(() => createSimulatorRegistry(simulatorApps), [simulatorApps]);
  const profileApps = useMemo(() => appsForProfile(registry, profile.id), [profile.id, registry]);
  const [commandLog, setCommandLog] = useState<ShellCommandLogEntry[]>([]);
  const [runOpen, setRunOpen] = useState(false);
  const bootedDefaultProfile = useRef<string | null>(null);

  const runCommand = useCallback(async (
    input: SimulatorCommandInput,
    options: { silent?: boolean } = {}
  ): Promise<SimulatorCommandResult> => {
    const app = findAppById(registry, input.appId) ?? {
      id: input.appId,
      launchCapability: input.capability,
      title: input.appId
    };
    const requestId = `fd-win11-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const request = buildSimulatorCommandRequest({
      activeMachine,
      app,
      capability: input.capability,
      commandType: input.commandType,
      payload: input.payload,
      profileId: profile.id,
      requestId,
      ticketId
    });

    try {
      const response = await fetch(`/api/v2/simulator/attempts/${encodeURIComponent(attemptId)}/commands`, {
        body: JSON.stringify(request),
        credentials: "include",
        headers: { "content-type": "application/json" },
        method: "POST"
      });
      const body = await response.json().catch(() => undefined);
      const result = commandResult({ body, ok: response.ok, requestId, status: response.status });
      setCommandLog((entries) => [{
        id: requestId,
        label: input.label ?? input.commandType,
        ok: result.ok,
        status: result.status
      }, ...entries].slice(0, 8));
      if (!options.silent) {
        onCommandMessage?.(result.ok
          ? `${input.label ?? input.commandType} is vastgelegd.`
          : `${input.label ?? input.commandType} is geweigerd (${result.status}).`);
      }
      onCommandComplete?.(result);
      return result;
    } catch (error) {
      const result = commandResult({
        body: { error: error instanceof Error ? error.message : "network_error" },
        ok: false,
        requestId,
        status: 0
      });
      setCommandLog((entries) => [{
        id: requestId,
        label: input.label ?? input.commandType,
        ok: false,
        status: 0
      }, ...entries].slice(0, 8));
      if (!options.silent) {
        onCommandMessage?.(`${input.label ?? input.commandType} kon niet worden verzonden.`);
      }
      onCommandComplete?.(result);
      return result;
    }
  }, [activeMachine, attemptId, onCommandComplete, onCommandMessage, profile.id, registry, ticketId]);

  const openApp = useCallback((appId: string, source: SimulatorLaunchSource, meta: Record<string, unknown> = {}) => {
    const app = findAppById(registry, appId);
    if (!app) return;
    const donorApp = toWin11DonorApp(app);
    dispatch({ type: donorApp.action, payload: "full" });
    dispatch({ type: "STARTHID" });
    void runCommand({
      appId: app.id,
      commandType: "OPEN_APP",
      capability: app.launchCapability ?? "simulator.open",
      label: `Open ${app.title}`,
      payload: {
        appId: app.id,
        launcher: source,
        machineId: activeMachine,
        profileId: profile.id,
        title: app.title,
        ...meta
      }
    }, { silent: true });
  }, [activeMachine, dispatch, profile.id, registry, runCommand]);

  const runAlias = useCallback((command: string, source: SimulatorLaunchSource) => {
    const app = resolveRunAlias(profileApps, command);
    if (!app) return false;
    openApp(app.id, source, { runCommand: command.trim() });
    return true;
  }, [openApp, profileApps]);

  useEffect(() => {
    dispatch({ type: "WALLBOOTED" });
    dispatch({
      type: "STNGSETV",
      payload: {
        path: "person.name",
        value: profile.machineRole === "server" || profile.machineRole === "server-core" ? "FirstDesk Administrator" : "FirstDesk Student"
      }
    });
    if (initialShellPhase === "desktop") {
      dispatch({ type: "WALLUNLOCK" });
    } else {
      dispatch({ type: "WALLALOCK" });
    }
    bootedDefaultProfile.current = null;
    setRunOpen(false);
  }, [dispatch, initialShellPhase, profile.id, profile.machineRole]);

  useEffect(() => {
    const bridgeWindow = window as BridgeWindow;
    bridgeWindow.__fdAttemptId = attemptId;
    bridgeWindow.fdBridge = {
      dispatchV2Command: (appId, commandType, capability, payload) => runCommand({
        appId,
        capability,
        commandType,
        payload
      })
    };
    return () => {
      if (bridgeWindow.__fdAttemptId === attemptId) {
        delete bridgeWindow.__fdAttemptId;
      }
      delete bridgeWindow.fdBridge;
    };
  }, [attemptId, runCommand]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r") {
        event.preventDefault();
        setRunOpen(true);
      }
      if (event.key === "Escape") {
        setRunOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!wall.booted || wall.locked) return;
    if (bootedDefaultProfile.current === profile.id) return;
    bootedDefaultProfile.current = profile.id;
    for (const appId of profile.defaultOpenAppIds) {
      openApp(appId, "profile-default");
    }
  }, [openApp, profile.defaultOpenAppIds, profile.id, wall.booted, wall.locked]);

  const hidePanels = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    const menu = target?.closest("[data-menu]")?.getAttribute("data-menu");
    if (menu !== "task") {
      dispatch({ type: "TASKPHIDE" });
    }
    if (menu !== "start") {
      dispatch({ type: "STARTHID" });
    }
    if (menu !== "pane") {
      dispatch({ type: "PANEHIDE" });
    }
    if (menu !== "widget") {
      dispatch({ type: "WIDGHIDE" });
    }
    if (menu !== "calendar") {
      dispatch({ type: "CALNHIDE" });
    }
    if (menu !== "context") {
      dispatch({ type: "MENUHIDE" });
    }
  }, [dispatch]);

  const openContextMenu = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const target = event.target instanceof Element ? event.target : null;
    const menu = target?.closest("[data-menu]")?.getAttribute("data-menu") ?? "desk";
    dispatch({
      type: "MENUSHOW",
      payload: {
        attr: target?.attributes ?? null,
        dataset: target instanceof HTMLElement ? { ...target.dataset } : null,
        left: event.clientX,
        menu,
        top: event.clientY
      }
    });
  }, [dispatch]);

  const unlockFromSignIn = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(".signInBtn")) {
      window.setTimeout(() => dispatch({ type: "WALLUNLOCK" }), 250);
    }
  }, [dispatch]);

  return (
    <div
      className={`fd-win11-sim fd-win11-sim--${profile.theme}`}
      data-profile={profile.id}
      data-simulator-runtime="fd-native"
      data-ui-baseline="firstdesk-desktop-shell"
      onClick={hidePanels}
      onContextMenu={openContextMenu}
    >
      <FirstDeskRegistryInstaller apps={profileApps} profile={profile} />
      <FirstDeskTelemetryBridge registry={registry} runCommand={runCommand} activeMachine={activeMachine} profileId={profile.id} />
      <div className="App">
        <div className="appwrap">
          <Background />
          {wall.booted && !wall.locked ? (
            <>
              <div className="desktop" data-menu="desk">
                <DesktopApp />
                <DonorApplicationWindows />
                <FirstDeskBridgeWindows
                  activeMachine={activeMachine}
                  commandLog={commandLog}
                  onOpenApp={openApp}
                  onRunAlias={runAlias}
                  onRunCommand={runCommand}
                  profileId={profile.id}
                  registry={registry}
                />
                <StartMenu />
                <SidePane />
                <WidPane />
                <CalnWid />
                {runOpen ? <Win11RunDialog onClose={() => setRunOpen(false)} onRunAlias={runAlias} /> : null}
              </div>
              <DonorTaskbar />
              <DonorActMenu />
            </>
          ) : wall.booted ? (
            <div className="fd-win11-lock-host" onClick={unlockFromSignIn}>
              <DonorLockScreen dir={wall.dir} />
              <button className="fd-win11-login-hitbox" type="button" aria-label="Sign in" onClick={() => dispatch({ type: "WALLUNLOCK" })} />
            </div>
          ) : (
            <BootScreen dir={wall.dir} />
          )}
        </div>
      </div>
    </div>
  );
}

function DonorApplicationWindows() {
  return (
    <>
      <BrowserApp />
      <Explorer />
      <Settings />
      <WnTerminal />
      <Calculator />
      <Notepad />
      <WhiteBoard />
      <Taskmanager />
      <CatalogApp />
      <Camera />
      <Getstarted />
    </>
  );
}

function FirstDeskRegistryInstaller({
  apps,
  profile
}: {
  apps: SimulatorAppDefinition[];
  profile: ReturnType<typeof getSimulatorProfile>;
}) {
  const dispatch = useDispatch<Dispatch>();
  const appsState = useSelector((state: DonorRootState) => state.apps);

  useEffect(() => {
    const donorApps = apps.map(toWin11DonorApp);
    for (const donorApp of donorApps) {
      if (typeof appsState[donorApp.icon] !== "object") {
        dispatch({ type: "ADDAPP", payload: donorApp });
      }
    }
    for (const donorApp of win11ProfileDesktopApps(apps, profile)) {
      dispatch({ type: "DESKADD", payload: donorApp });
    }
    for (const donorApp of win11ProfilePinnedApps(apps, profile)) {
      dispatch({ type: "STARTPIN", payload: donorApp });
      if (profile.pinnedAppIds.includes(donorApp.fdAppId)) {
        dispatch({ type: "TASKADD", payload: donorApp });
      }
    }
  }, [apps, appsState, dispatch, profile]);

  return null;
}

function FirstDeskTelemetryBridge({
  activeMachine,
  profileId,
  registry,
  runCommand
}: {
  activeMachine: string;
  profileId: SimulatorProfileId;
  registry: SimulatorAppDefinition[];
  runCommand: (input: SimulatorCommandInput, options?: { silent?: boolean }) => Promise<SimulatorCommandResult>;
}) {
  const appsState = useSelector((state: DonorRootState) => state.apps);
  const reportedOpenApps = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const entry of Object.values(appsState)) {
      if (!isDonorAppWindow(entry) || entry.hide || !entry.max) continue;
      const appId = entry.fdAppId ?? firstDeskAppIdForDonorAction(entry.action, entry.icon);
      if (!appId || reportedOpenApps.current.has(appId)) continue;
      const app = findAppById(registry, appId);
      if (!app) continue;
      reportedOpenApps.current.add(appId);
      void runCommand({
        appId,
        commandType: "OPEN_APP",
        capability: app.launchCapability ?? "simulator.open",
        label: `Open ${app.title}`,
        payload: {
          appId,
          launcher: "start",
          machineId: activeMachine,
          profileId,
          title: app.title
        }
      }, { silent: true });
    }
  }, [activeMachine, appsState, profileId, registry, runCommand]);

  return null;
}

function FirstDeskBridgeWindows({
  activeMachine,
  commandLog,
  onOpenApp,
  onRunAlias,
  onRunCommand,
  profileId,
  registry
}: {
  activeMachine: string;
  commandLog: ShellCommandLogEntry[];
  onOpenApp: (appId: string, source: SimulatorLaunchSource, meta?: Record<string, unknown>) => void;
  onRunAlias: (command: string, source: SimulatorLaunchSource) => boolean;
  onRunCommand: (input: SimulatorCommandInput) => Promise<SimulatorCommandResult>;
  profileId: SimulatorProfileId;
  registry: SimulatorAppDefinition[];
}) {
  const appsState = useSelector((state: DonorRootState) => state.apps);
  const profile = getSimulatorProfile(profileId);

  return (
    <>
      {Object.values(appsState).map((entry) => {
        if (!isDonorAppWindow(entry) || !entry.fdAppId || isWin11NativeDonorApp(entry.fdAppId)) return null;
        const app = findAppById(registry, entry.fdAppId);
        if (!app) return null;
        return (
          <div
            className="fd-win11-fd-window lightWindow floatTab dpShad"
            data-hide={entry.hide}
            data-max={entry.max}
            data-size={entry.size}
            id={`${entry.icon}App`}
            key={entry.icon}
            style={{
              ...(entry.size === "cstm" ? entry.dim : null),
              zIndex: entry.z
            }}
          >
            <ToolBar app={entry.action} icon={entry.icon} name={entry.name} size={entry.size} />
            <div className="windowScreen flex flex-col" data-dock="true">
              <div className="fd-app-surface">
                <AppSurface
                  activeMachine={activeMachine}
                  app={app}
                  commandLog={commandLog}
                  onOpenApp={onOpenApp}
                  onRunAlias={onRunAlias}
                  onRunCommand={onRunCommand}
                  profile={profile}
                />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

function Win11RunDialog({
  onClose,
  onRunAlias
}: {
  onClose: () => void;
  onRunAlias: (command: string, source: SimulatorLaunchSource) => boolean;
}) {
  const [command, setCommand] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (onRunAlias(command, "run")) {
      onClose();
      return;
    }
    setError("Deze opdracht is nog niet gekoppeld aan een simulator-app.");
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="fd-win11-run" data-menu="run" onClick={(event) => event.stopPropagation()} onKeyDown={onKeyDown} role="dialog" aria-modal="true" aria-label="Run">
      <form onSubmit={submit}>
        <div className="fd-win11-run__title">Run</div>
        <div className="fd-win11-run__hint">Type the name of a program, folder, document, or simulator command.</div>
        <div className="fd-win11-run__row">
          <label htmlFor="fd-win11-run-input">Open:</label>
          <input
            autoFocus
            id="fd-win11-run-input"
            onChange={(event) => {
              setCommand(event.target.value);
              setError(null);
            }}
            value={command}
          />
        </div>
        {error ? <div className="fd-win11-run__hint">{error}</div> : null}
        <div className="fd-win11-run__actions">
          <button type="submit">OK</button>
          <button type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function isDonorAppWindow(value: DonorAppWindowState | number | undefined): value is DonorAppWindowState {
  return typeof value === "object" && value !== null && "action" in value && "icon" in value;
}
